/**
 * Paging helpers for the `/entity/selector` endpoint.
 *
 * The endpoint applies its page window in SQL and *then* filters the rows in
 * Java (rows whose treePath does not match one of the parent template's
 * relations are dropped, and repeated entityIds are collapsed). A full database
 * page of `pageSize` rows can therefore come back shorter than `pageSize` while
 * further pages still exist — 27 rows for a requested 30 out of ~3,900
 * available loggers, in the UBQ-5573 report.
 *
 * That makes `response.length === pageSize` unusable as an end-of-data signal.
 * The only signal the response shape actually supports is a page that
 * contributes no new records, which is what this helper reports.
 */

export interface SelectorPageMergeResult<T> {
    /** The accumulated list. Returned by reference when nothing was added. */
    records: T[]
    /** Whether another page is worth requesting. */
    hasMore: boolean
}

const entityKey = (record: unknown): string | undefined => {
    const entityId = (record as { entityId?: unknown })?.entityId
    return typeof entityId === 'string' && entityId ? entityId : undefined
}

/**
 * Merge one page of selector results into the list loaded so far.
 *
 * @param previousRecords the list accumulated by earlier pages
 * @param incomingPage the raw payload for this page; `fetchEntityRecordsListBySearch`
 *        resolves with the axios error response on failure, so this is not
 *        guaranteed to be an array
 * @param isFirstPage true when this page replaces the list rather than extending it
 */
export const mergeSelectorPage = <T>(
    previousRecords: T[],
    incomingPage: unknown,
    isFirstPage: boolean,
): SelectorPageMergeResult<T> => {
    const page: T[] = Array.isArray(incomingPage) ? incomingPage : []

    if (isFirstPage) {
        return { records: page, hasMore: page.length > 0 }
    }

    const seen = new Set(
        previousRecords.map(entityKey).filter((key): key is string => !!key),
    )
    /* Records without an entityId cannot be compared, so they are kept rather
       than silently dropped. */
    const freshRecords = page.filter((record) => {
        const key = entityKey(record)
        return key === undefined || !seen.has(key)
    })

    if (freshRecords.length === 0) {
        return { records: previousRecords, hasMore: false }
    }

    return {
        records: [...previousRecords, ...freshRecords],
        hasMore: true,
    }
}
